import {
	ComponentMirror,
	Injectable,
	InjectionToken,
	Injector,
	reflectComponentType,
	Type,
	ViewContainerRef,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { ignoreNil } from "@obg/ngx.global";
import { RouteConfig, RouteSelector } from "@obg/ngx.router";
import { EMPTY, Observable, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map, switchMap, take } from "rxjs/operators";

import { DynamicLayoutHandlerAttachOptions } from "./dynamic-layout.model";

@Injectable({
	providedIn: "root",
})
export class DynamicLayoutHandler {
	private componentInjectorMap = new Map<Type<unknown>, Injector>();
	private data$$: Subscription | undefined;
	private currentLayout: ComponentMirror<unknown> | undefined;

	constructor(
		private injector: Injector,
		private store: Store<any>
	) {}

	attach(viewContainerRef: ViewContainerRef, options?: DynamicLayoutHandlerAttachOptions) {
		if (this.data$$) {
			return;
		}

		this.data$$ = this.getRouteLayoutComponent(viewContainerRef).subscribe(
			(layout) => this.setLayout(layout, viewContainerRef),
			options?.errorHandler
		);
	}

	detach() {
		this.data$$?.unsubscribe();
	}

	dispose() {
		this.data$$ = undefined;
		this.currentLayout = undefined;
	}

	getLayoutFromRoute(route: RouteConfig | undefined): Type<any> | undefined {
		const layoutOrToken = route?.layout;
		return layoutOrToken instanceof InjectionToken ? this.injector.get(layoutOrToken) : layoutOrToken;
	}

	registerComponentInjector(component: Type<unknown>, injector: Injector) {
		this.componentInjectorMap.set(component, injector);
	}

	private setLayout(component: Type<unknown>, viewContainerRef: ViewContainerRef) {
		const componentRef = viewContainerRef.createComponent(component, {
			injector: this.componentInjectorMap.get(component),
		});
		componentRef.changeDetectorRef.markForCheck();
		componentRef.onDestroy(() => componentRef.hostView.detectChanges());
		this.currentLayout = reflectComponentType(component) ?? undefined;
	}

	private getRouteLayoutComponent(viewContainerRef: ViewContainerRef): Observable<Type<any>> {
		return this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			distinctUntilChanged((x, y) => x.layout === y.layout),
			switchMap((route) => {
				const component = this.getLayoutFromRoute(route);
				if (!component || this.currentLayout?.selector === reflectComponentType(component)?.selector) {
					return EMPTY; // keep previous layout in case we don't have a new one, or it's not changed
				}
				// clear so we don't get events from previous route while waiting for navigation to finish
				viewContainerRef.clear();

				// wait for navigation to finish before changing the layout
				// otherwise components will be wastefully reconstructed and trigger faulty initialisation logic
				return this.store.select(RouteSelector.isNavigating).pipe(
					filter((isNavigating) => !isNavigating),
					take(1),
					map(() => component)
				);
			}),
			ignoreNil()
		);
	}
}
